import { debounce } from 'lodash';
import {
	MutableRefObject,
	ReactElement,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import FocusLock from 'react-focus-lock';
import { useTheme } from 'styled-components';

import { CalmTheme, gradients } from '../ThemeProvider';
import {
	BackIcon,
	ChildContainer,
	CloseIcon,
	ModalAndOverlayContainer,
	ModalBody,
	ModalContainer,
	ModalFooter,
	Overlay,
	OverlayClickHandler,
	Title,
	TitleContainer,
} from './styles';
import { ModalProps, ModalWidth } from './types';

type PresentationalModalProps = Omit<ModalProps, 'isOpen' | 'closeModal'> & {
	onClose: () => void;
	hasOverflowY?: boolean;
	$UNSAFE_positionAbsolute?: boolean;
	$UNSAFE_disableAutofocus?: boolean;
};

export const PresentationalModal = ({
	canClose,
	onClose,
	children,
	additionalOverlay,
	onBack,
	showBackButton,
	title,
	titleElement,
	footer,
	borderColor = 'modalBorder',
	backgroundColor = 'modalBg',
	width = ModalWidth.Normal,
	noPadding = false,
	'aria-label': ariaLabel,
	'aria-labelledby': ariaLabelledBy,
	$UNSAFE_positionAbsolute,
	$UNSAFE_disableAutofocus,
	hasOverflowY = true,
	showTooltip = true,
	...props
}: PresentationalModalProps): ReactElement => {
	const fallbackFocus = useRef(null);
	const theme = useTheme();
	const onKeyDown = useCallback(
		(event: KeyboardEvent): void => {
			if (event.key === 'Escape' && canClose) {
				onClose();
			}
		},
		[canClose, onClose],
	);
	useEffect(() => {
		document.addEventListener('keydown', onKeyDown);
		return () => {
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [onKeyDown]);

	const [showClickHandler, setShowClickHandler] = useState(false);
	useEffect(() => {
		if (process.env.NODE_ENV === 'test') return;
		// This gross bit of code is necessary to allow the "touch event" for buttons that trigger this modal to not trigger the overlay click handler. The issue is caused by the "@react-aria/button" package. When using 'legacyOnClick' this issue does not occur. Rather than pollute our buttons with legacy handlers, we'll isolate the grossness here until we can find a better workaround/fix.
		const timeout = setTimeout(() => setShowClickHandler(true), 50);
		return () => clearTimeout(timeout);
	}, []);
	const contentRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
	const [showFooterBorder, setShowFooterBorder] = useState(false);
	useLayoutEffect(() => {
		const { current: contentEl } = contentRef;
		if (!contentEl) {
			return;
		}
		setShowFooterBorder(contentEl.scrollHeight > contentEl.clientHeight);
	}, []);
	const onScroll = useMemo(() => debounce(() => setShowFooterBorder(true), 15), []);

	const isGradientBorderColor = Object.keys(gradients).includes(borderColor);
	const titleIsCentered = (theme as typeof CalmTheme).modal.titleAlign === 'center';
	const hasTitle = Boolean(title || titleElement);

	return (
		<ModalAndOverlayContainer $UNSAFE_positionAbsolute={$UNSAFE_positionAbsolute}>
			<Overlay />
			{additionalOverlay}
			{showClickHandler && <OverlayClickHandler onClick={onClose} />}
			<FocusLock autoFocus={!$UNSAFE_disableAutofocus}>
				<ModalContainer $width={width} {...props}>
					<ModalBody
						role="dialog"
						aria-modal
						ref={fallbackFocus}
						tabIndex={-1}
						title={showTooltip ? title : undefined}
						aria-label={ariaLabel}
						aria-labelledby={ariaLabelledBy}
						$isGradientBorderColor={isGradientBorderColor}
						$borderColor={borderColor}
						$backgroundColor={backgroundColor}
					>
						{hasTitle && (
							<TitleContainer
								$titleIsCentered={titleIsCentered}
								$hasLeftButton={showBackButton}
								$hasRightButton={canClose}
							>
								{titleElement ?? <Title>{title}</Title>}
							</TitleContainer>
						)}
						<ChildContainer
							$hasTopButtons={Boolean(canClose || showBackButton)}
							$hasTitle={hasTitle}
							$noPadding={noPadding}
							$hasOverflowY={hasOverflowY}
							onScroll={onScroll}
							ref={contentRef}
						>
							{children}
						</ChildContainer>
						{footer && <ModalFooter topBorder={showFooterBorder}>{footer}</ModalFooter>}
						{canClose && <CloseIcon aria-label="close" data-testid="modal-close" onPress={onClose} />}
						{showBackButton && <BackIcon aria-label="back" onPress={onBack} />}
					</ModalBody>
				</ModalContainer>
			</FocusLock>
		</ModalAndOverlayContainer>
	);
};

const Modal = ({
	isOpen,
	closeModal,
	canClose = true,
	hasOverflowY = true,
	borderColor,
	backgroundColor,
	...props
}: ModalProps): ReactElement | null => {
	const [isMounted, setIsMounted] = useState(false);
	const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

	const onClose = useCallback(() => {
		if (canClose) {
			closeModal();
		}
	}, [canClose, closeModal]);

	useEffect(() => {
		if (isOpen) {
			const container = document.createElement('div');
			container.id = 'modal-container';
			document.body.appendChild(container);
			// TODO: refactor this code to follow best practices
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setPortalContainer(container);
			setIsMounted(true);

			return () => {
				setIsMounted(false);
				container.remove();
				setPortalContainer(null);
			};
		}
	}, [isOpen]);

	if (!isMounted || !portalContainer || !isOpen) {
		return null;
	}

	return createPortal(
		<PresentationalModal
			{...props}
			onClose={onClose}
			canClose={canClose}
			hasOverflowY={hasOverflowY}
			borderColor={borderColor}
			backgroundColor={backgroundColor}
		/>,
		portalContainer,
	);
};

export default Modal;
